import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Users,
  Target,
  Heart,
  Award,
  Globe,
  Shield,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Linkedin,
  Instagram,
  ExternalLink,
  Play,
  Calendar,
  MapPin,
  Building,
  Handshake,
  Lightbulb,
  Eye,
  Compass,
  Star,
  Quote,
  ThumbsUp,
  MessageCircle,
  Share2,
  Clock
} from 'lucide-react';

// Animated counter
const AnimatedCounter = ({ end, suffix = '', duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return <span>{count}{suffix}</span>;
};

const AboutPage = () => {
  const { language } = useLanguage();
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    pt: {
      hero: {
        badge: 'QUEM SOMOS',
        title: 'Sua Parceira em',
        titleHighlight: 'Comércio Exterior',
        subtitle: 'Há mais de 15 anos conectando o Brasil ao mundo com soluções logísticas inteligentes, atendimento consultivo e compromisso com resultados.',
      },
      intro: {
        title: 'Sobre a',
        titleHighlight: 'Well Cargo',
        paragraphs: [
          'A Well Cargo é uma empresa especializada em logística internacional que combina inteligência em comércio exterior com operações de alto desempenho. Atuamos como extensão da equipe dos nossos clientes, oferecendo soluções personalizadas e suporte consultivo em cada etapa do processo — do planejamento à entrega.',
          'Com mais de 15 anos de experiência no setor, nossa equipe alia domínio técnico, agilidade e proximidade no atendimento, proporcionando previsibilidade, segurança e eficiência operacional em todas as operações. Nossa atuação é nacional e internacional, com uma ampla rede de parceiros logísticos e presença em 196 países por meio da WCA World, a maior rede global de agentes de cargas.',
          'Nosso propósito é descomplicar o comércio exterior, tornando-o mais leve, acessível e seguro com a parceria certa. Acreditamos que um serviço logístico de excelência vai além da execução: envolve relacionamento, comprometimento e visão estratégica.',
        ],
      },
      stats: {
        items: [
          { value: 15, suffix: '+', label: 'Anos de mercado' },
          { value: 500, suffix: '+', label: 'Clientes ativos' },
          { value: 95, suffix: '+', label: 'Países atendidos' },
          { value: 24, suffix: 'Mil', label: 'Contêineres movimentados' },
        ],
      },
      mission: {
        title: 'Nosso',
        titleHighlight: 'Propósito',
        items: [
          {
            icon: Target,
            title: 'Missão',
            description: 'Oferecer soluções logísticas personalizadas que integrem inovação, conhecimento técnico e proximidade no atendimento, promovendo um comércio exterior mais fluido, dinâmico e acessível, com foco na eficiência dos processos e nas necessidades específicas de cada cliente.',
          },
          {
            icon: Eye,
            title: 'Visão',
            description: 'Expandir a marca Well Cargo no mercado brasileiro, sendo uma empresa reconhecida por sua excelência no gerenciamento de processos logísticos e agenciamento de cargas em termos de confiabilidade, segurança, eficiência operacional e responsabilidade em nossas operações.',
          },
          {
            icon: Heart,
            title: 'Valores',
            description: 'Colaboração e trabalho em equipe: Acreditamos que a integração entre setores fortalece nossas operações e melhora a qualidade do serviço prestado. Foco em resultados: Buscamos gerar impacto positivo para clientes e parceiros, garantindo eficiência operacional e sustentabilidade financeira. Ética e transparência: Atuamos com integridade em todas as nossas relações, prezando pela clareza e honestidade. Inovação e evolução contínua: Valorizamos a criatividade e a busca constante por melhorias que tornem nossos processos mais eficientes e competitivos.',
          },
        ],
      },
      values: {
        title: 'O que nos',
        titleHighlight: 'move',
        items: [
          { icon: Handshake, title: 'Transparência', desc: 'Relações claras e honestas com clientes e parceiros' },
          { icon: Target, title: 'Comprometimento', desc: 'Dedicação total ao sucesso de cada operação' },
          { icon: TrendingUp, title: 'Agilidade', desc: 'Respostas rápidas e soluções eficientes' },
          { icon: Lightbulb, title: 'Inovação', desc: 'Busca constante por melhorias e novas soluções' },
          { icon: Users, title: 'Foco no Cliente', desc: 'Suas necessidades são nossa prioridade' },
          { icon: Shield, title: 'Confiabilidade', desc: 'Segurança e previsibilidade em cada entrega' },
        ],
      },
      timeline: {
        title: 'Nossa',
        titleHighlight: 'Trajetória',
        items: [
          { year: '2009', title: 'Fundação', desc: 'Início das operações em Vila Velha/ES com foco em transporte marítimo' },
          { year: '2012', title: 'Expansão Sul', desc: 'Abertura do escritório em Itajaí/SC para atender a demanda regional' },
          { year: '2015', title: 'WCA World', desc: 'Ingresso na maior rede global de agentes de carga, com presença em 196 países' },
          { year: '2018', title: 'Certificação', desc: 'Obtenção da certificação ISO 9001 para gestão de qualidade' },
          { year: '2021', title: 'Sudeste', desc: 'Abertura dos escritórios em Belo Horizonte e São Paulo' },
          { year: '2024', title: 'Transformação Digital', desc: 'Lançamento de plataforma digital para rastreamento e cotações online' },
        ],
      },
      offices: {
        title: 'Nossos',
        titleHighlight: 'Escritórios',
        locations: [
          { city: 'Vila Velha/ES', type: 'Matriz', address: 'Rua Sete de Junho, 33, Sala 506, Coqueiral de Itaparica – CEP 29.102-310' },
          { city: 'Belo Horizonte/MG', type: 'Filial', address: 'Rua Tomé de Souza, 830, Sala 1602, Savassi – CEP 30.140-136' },
          { city: 'Itajaí/SC', type: 'Filial', address: 'Rua Samuel Heusi, nº 463, Sala 606, Centro – CEP 88.301-320' },
          { city: 'São Paulo/SP', type: 'Filial', address: 'Avenida Paulista, 1439, Ed. Mario Wallace Simonsen Cochrane, 1º andar, cj 12 – CEP 01.311-200' },
        ],
      },
      partnerships: {
        title: 'Parceiros e',
        titleHighlight: 'Certificações',
        items: [
          { name: 'WCA World', desc: 'Maior rede global de agentes de carga, com presença em 196 países', logo: 'https://customer-assets.emergentagent.com/job_global-shipping-22/artifacts/s8xmakb6_SELO%20WCA%20WORLD.jpg' },
          { name: 'IATA', desc: 'Associação Internacional de Transporte Aéreo', logo: 'https://customer-assets.emergentagent.com/job_global-shipping-22/artifacts/mlok2810_SELO%20IATA%20.png' },
        ],
      },
      social: {
        title: 'Acompanhe a',
        titleHighlight: 'Well Cargo',
        subtitle: 'Fique por dentro das novidades do comércio exterior, dicas de logística e bastidores da nossa operação.',
        linkedin: {
          title: 'LinkedIn',
          handle: '@wellcargobr',
          url: 'https://www.linkedin.com/company/well-cargo/',
          followers: '2.5K',
          description: 'Insights sobre comércio exterior, tendências do mercado e novidades da empresa',
          posts: [
            {
              type: 'article',
              title: '5 tendências de logística internacional para 2025',
              engagement: '234 curtidas • 45 comentários',
              time: 'Há 2 dias',
            },
            {
              type: 'update',
              title: 'Novo escritório em São Paulo! Expandindo nossa presença para melhor atendê-los.',
              engagement: '189 curtidas • 32 comentários',
              time: 'Há 1 semana',
            },
            {
              type: 'tips',
              title: 'Como reduzir custos no transporte marítimo: guia completo',
              engagement: '312 curtidas • 67 comentários',
              time: 'Há 2 semanas',
            },
          ],
        },
        instagram: {
          title: 'Instagram',
          handle: '@wellcargobr',
          url: 'https://www.instagram.com/wellcargobr/',
          followers: '1.8K',
          description: 'Bastidores, equipe, operações e cultura Well Cargo',
          posts: [
            {
              image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400&q=80',
              likes: '156',
              caption: 'Mais um embarque rumo à Europa! 🚢',
            },
            {
              image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80',
              likes: '203',
              caption: 'Velocidade que faz a diferença ✈️',
            },
            {
              image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&q=80',
              likes: '178',
              caption: 'Time Well Cargo reunido! 💚',
            },
            {
              image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&q=80',
              likes: '142',
              caption: 'Cobertura nacional 🚛',
            },
          ],
        },
        cta: 'Siga-nos',
      },
      team: {
        title: 'Nossa',
        titleHighlight: 'Equipe',
        subtitle: 'Profissionais apaixonados por logística e comprometidos com o seu sucesso.',
        stats: [
          { value: '40+', label: 'Colaboradores' },
          { value: '8+', label: 'Anos de experiência média' },
          { value: '100%', label: 'Dedicação' },
        ],
      },
      testimonials: {
        title: 'O que dizem',
        titleHighlight: 'nossos clientes',
        items: [
          {
            quote: 'A Well Cargo não é apenas um prestador de serviços, é um parceiro estratégico. O suporte consultivo fez toda diferença na nossa expansão internacional.',
            author: 'Carlos Mendes',
            role: 'CEO',
            company: 'TechBras Exportadora',
          },
          {
            quote: 'Profissionalismo, transparência e resultados. É o que encontramos na Well Cargo há mais de 5 anos de parceria.',
            author: 'Ana Paula Silva',
            role: 'Gerente de Supply Chain',
            company: 'Indústria Nacional',
          },
        ],
      },
      cta: {
        title: 'Vamos crescer juntos?',
        subtitle: 'Entre em contato e descubra como podemos impulsionar sua operação de comércio exterior.',
        button: 'Falar com a equipe',
      },
    },
    en: {
      hero: {
        badge: 'ABOUT US',
        title: 'Your Partner in',
        titleHighlight: 'Foreign Trade',
        subtitle: 'For over 15 years connecting Brazil to the world with intelligent logistics solutions, consultative service and commitment to results.',
      },
      intro: {
        title: 'About',
        titleHighlight: 'Well Cargo',
        paragraphs: [
          'Well Cargo is a company specialized in international logistics that combines foreign trade intelligence with high-performance operations. We act as an extension of our clients\' team, offering personalized solutions and consultative support at every stage of the process — from planning to delivery.',
          'With over 15 years of experience in the sector, our team combines technical expertise, agility and closeness in service, providing predictability, security and operational efficiency in all operations. Our reach is national and international, with a wide network of logistics partners and presence in 196 countries through WCA World, the largest global network of cargo agents.',
          'Our purpose is to simplify foreign trade, making it lighter, more accessible and safer with the right partnership. We believe that an excellent logistics service goes beyond execution: it involves relationship, commitment and strategic vision.',
        ],
      },
      stats: {
        items: [
          { value: 15, suffix: '+', label: 'Years in market' },
          { value: 500, suffix: '+', label: 'Active clients' },
          { value: 95, suffix: '+', label: 'Countries served' },
          { value: 24, suffix: 'K', label: 'Containers moved' },
        ],
      },
      mission: {
        title: 'Our',
        titleHighlight: 'Purpose',
        items: [
          {
            icon: Target,
            title: 'Mission',
            description: 'Offer personalized logistics solutions that integrate innovation, technical knowledge and close service, promoting a more fluid, dynamic and accessible foreign trade, focused on process efficiency and the specific needs of each client.',
          },
          {
            icon: Eye,
            title: 'Vision',
            description: 'Expand the Well Cargo brand in the Brazilian market, being a company recognized for its excellence in logistics process management and cargo agency in terms of reliability, security, operational efficiency and responsibility in our operations.',
          },
          {
            icon: Heart,
            title: 'Values',
            description: 'Collaboration and teamwork: We believe that integration between sectors strengthens our operations and improves service quality. Focus on results: We seek to generate positive impact for clients and partners, ensuring operational efficiency and financial sustainability. Ethics and transparency: We act with integrity in all our relationships, valuing clarity and honesty. Innovation and continuous evolution: We value creativity and the constant pursuit of improvements that make our processes more efficient and competitive.',
          },
        ],
      },
      values: {
        title: 'What',
        titleHighlight: 'drives us',
        items: [
          { icon: Handshake, title: 'Transparency', desc: 'Clear and honest relationships' },
          { icon: Target, title: 'Commitment', desc: 'Total dedication to success' },
          { icon: TrendingUp, title: 'Agility', desc: 'Quick responses and solutions' },
          { icon: Lightbulb, title: 'Innovation', desc: 'Constant search for improvements' },
          { icon: Users, title: 'Customer Focus', desc: 'Your needs are our priority' },
          { icon: Shield, title: 'Reliability', desc: 'Security in every delivery' },
        ],
      },
      timeline: {
        title: 'Our',
        titleHighlight: 'Journey',
        items: [
          { year: '2009', title: 'Foundation', desc: 'Start of operations in Vila Velha/ES focused on sea transport' },
          { year: '2012', title: 'South Expansion', desc: 'Opening of Itajaí/SC office to meet regional demand' },
          { year: '2015', title: 'WCA World', desc: 'Joining the largest global network of cargo agents, with presence in 196 countries' },
          { year: '2018', title: 'Certification', desc: 'ISO 9001 certification for quality management' },
          { year: '2021', title: 'Southeast', desc: 'Opening of Belo Horizonte and São Paulo offices' },
          { year: '2024', title: 'Digital Transformation', desc: 'Launch of digital platform for tracking and online quotes' },
        ],
      },
      offices: {
        title: 'Our',
        titleHighlight: 'Offices',
        locations: [
          { city: 'Vila Velha/ES', type: 'Headquarters', address: 'Rua Sete de Junho, 33, Sala 506, Coqueiral de Itaparica – CEP 29.102-310' },
          { city: 'Belo Horizonte/MG', type: 'Branch', address: 'Rua Tomé de Souza, 830, Sala 1602, Savassi – CEP 30.140-136' },
          { city: 'Itajaí/SC', type: 'Branch', address: 'Rua Samuel Heusi, nº 463, Sala 606, Centro – CEP 88.301-320' },
          { city: 'São Paulo/SP', type: 'Branch', address: 'Avenida Paulista, 1439, Ed. Mario Wallace Simonsen Cochrane, 1º andar, cj 12 – CEP 01.311-200' },
        ],
      },
      partnerships: {
        title: 'Partners &',
        titleHighlight: 'Certifications',
        items: [
          { name: 'WCA World', desc: 'Largest global network of cargo agents, with presence in 196 countries', logo: 'https://customer-assets.emergentagent.com/job_global-shipping-22/artifacts/s8xmakb6_SELO%20WCA%20WORLD.jpg' },
          { name: 'IATA', desc: 'International Air Transport Association', logo: 'https://customer-assets.emergentagent.com/job_global-shipping-22/artifacts/mlok2810_SELO%20IATA%20.png' },
        ],
      },
      social: {
        title: 'Follow',
        titleHighlight: 'Well Cargo',
        subtitle: 'Stay updated on foreign trade news, logistics tips and behind the scenes of our operation.',
        linkedin: {
          title: 'LinkedIn',
          handle: '@wellcargobr',
          url: 'https://www.linkedin.com/company/well-cargo/',
          followers: '2.5K',
          description: 'Insights on foreign trade, market trends and company news',
          posts: [
            {
              type: 'article',
              title: '5 international logistics trends for 2025',
              engagement: '234 likes • 45 comments',
              time: '2 days ago',
            },
            {
              type: 'update',
              title: 'New office in São Paulo! Expanding our presence to better serve you.',
              engagement: '189 likes • 32 comments',
              time: '1 week ago',
            },
            {
              type: 'tips',
              title: 'How to reduce costs in maritime transport: complete guide',
              engagement: '312 likes • 67 comments',
              time: '2 weeks ago',
            },
          ],
        },
        instagram: {
          title: 'Instagram',
          handle: '@wellcargobr',
          url: 'https://www.instagram.com/wellcargobr/',
          followers: '1.8K',
          description: 'Behind the scenes, team, operations and Well Cargo culture',
          posts: [
            {
              image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400&q=80',
              likes: '156',
              caption: 'Another shipment heading to Europe! 🚢',
            },
            {
              image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80',
              likes: '203',
              caption: 'Speed that makes a difference ✈️',
            },
            {
              image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&q=80',
              likes: '178',
              caption: 'Well Cargo team together! 💚',
            },
            {
              image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&q=80',
              likes: '142',
              caption: 'National coverage 🚛',
            },
          ],
        },
        cta: 'Follow us',
      },
      team: {
        title: 'Our',
        titleHighlight: 'Team',
        subtitle: 'Professionals passionate about logistics and committed to your success.',
        stats: [
          { value: '40+', label: 'Employees' },
          { value: '8+', label: 'Years avg experience' },
          { value: '100%', label: 'Dedication' },
        ],
      },
      testimonials: {
        title: 'What our',
        titleHighlight: 'clients say',
        items: [
          {
            quote: 'Well Cargo is not just a service provider, it\'s a strategic partner. The consultative support made all the difference in our international expansion.',
            author: 'Carlos Mendes',
            role: 'CEO',
            company: 'TechBras Exporter',
          },
          {
            quote: 'Professionalism, transparency and results. That\'s what we\'ve found at Well Cargo for over 5 years of partnership.',
            author: 'Ana Paula Silva',
            role: 'Supply Chain Manager',
            company: 'National Industry',
          },
        ],
      },
      cta: {
        title: 'Let\'s grow together?',
        subtitle: 'Get in touch and discover how we can boost your foreign trade operation.',
        button: 'Talk to the team',
      },
    },
    es: {
      hero: {
        badge: 'QUIÉNES SOMOS',
        title: 'Tu Socio en',
        titleHighlight: 'Comercio Exterior',
        subtitle: 'Por más de 15 años conectando Brasil al mundo con soluciones logísticas inteligentes, atención consultiva y compromiso con resultados.',
      },
      intro: {
        title: 'Sobre',
        titleHighlight: 'Well Cargo',
        paragraphs: [
          'Well Cargo es una empresa especializada en logística internacional que combina inteligencia en comercio exterior con operaciones de alto rendimiento. Actuamos como extensión del equipo de nuestros clientes, ofreciendo soluciones personalizadas y soporte consultivo en cada etapa del proceso — desde la planificación hasta la entrega.',
          'Con más de 15 años de experiencia en el sector, nuestro equipo combina dominio técnico, agilidad y cercanía en la atención, proporcionando previsibilidad, seguridad y eficiencia operacional en todas las operaciones. Nuestra actuación es nacional e internacional, con una amplia red de socios logísticos y presencia en 196 países a través de WCA World, la mayor red global de agentes de carga.',
          'Nuestro propósito es simplificar el comercio exterior, haciéndolo más ligero, accesible y seguro con la asociación correcta. Creemos que un servicio logístico de excelencia va más allá de la ejecución: involucra relación, compromiso y visión estratégica.',
        ],
      },
      stats: {
        items: [
          { value: 15, suffix: '+', label: 'Años de mercado' },
          { value: 500, suffix: '+', label: 'Clientes activos' },
          { value: 95, suffix: '+', label: 'Países atendidos' },
          { value: 24, suffix: 'Mil', label: 'Contenedores movidos' },
        ],
      },
      mission: {
        title: 'Nuestro',
        titleHighlight: 'Propósito',
        items: [
          {
            icon: Target,
            title: 'Misión',
            description: 'Ofrecer soluciones logísticas personalizadas que integren innovación, conocimiento técnico y cercanía en la atención, promoviendo un comercio exterior más fluido, dinámico y accesible, con enfoque en la eficiencia de los procesos y las necesidades específicas de cada cliente.',
          },
          {
            icon: Eye,
            title: 'Visión',
            description: 'Expandir la marca Well Cargo en el mercado brasileño, siendo una empresa reconocida por su excelencia en la gestión de procesos logísticos y agenciamiento de cargas en términos de confiabilidad, seguridad, eficiencia operacional y responsabilidad en nuestras operaciones.',
          },
          {
            icon: Heart,
            title: 'Valores',
            description: 'Colaboración y trabajo en equipo: Creemos que la integración entre sectores fortalece nuestras operaciones y mejora la calidad del servicio. Enfoque en resultados: Buscamos generar impacto positivo para clientes y socios, garantizando eficiencia operacional y sustentabilidad financiera. Ética y transparencia: Actuamos con integridad en todas nuestras relaciones, valorando la claridad y honestidad. Innovación y evolución continua: Valoramos la creatividad y la búsqueda constante de mejoras que hagan nuestros procesos más eficientes y competitivos.',
          },
        ],
      },
      values: {
        title: 'Lo que nos',
        titleHighlight: 'mueve',
        items: [
          { icon: Handshake, title: 'Transparencia', desc: 'Relaciones claras y honestas' },
          { icon: Target, title: 'Compromiso', desc: 'Dedicación total al éxito' },
          { icon: TrendingUp, title: 'Agilidad', desc: 'Respuestas y soluciones rápidas' },
          { icon: Lightbulb, title: 'Innovación', desc: 'Búsqueda constante de mejoras' },
          { icon: Users, title: 'Enfoque al Cliente', desc: 'Sus necesidades son nuestra prioridad' },
          { icon: Shield, title: 'Confiabilidad', desc: 'Seguridad en cada entrega' },
        ],
      },
      timeline: {
        title: 'Nuestra',
        titleHighlight: 'Trayectoria',
        items: [
          { year: '2009', title: 'Fundación', desc: 'Inicio de operaciones en Vila Velha/ES enfocado en transporte marítimo' },
          { year: '2012', title: 'Expansión Sur', desc: 'Apertura de oficina en Itajaí/SC para atender demanda regional' },
          { year: '2015', title: 'WCA World', desc: 'Ingreso a la mayor red global de agentes de carga, con presencia en 196 países' },
          { year: '2018', title: 'Certificación', desc: 'Certificación ISO 9001 para gestión de calidad' },
          { year: '2021', title: 'Sudeste', desc: 'Apertura de oficinas en Belo Horizonte y São Paulo' },
          { year: '2024', title: 'Transformación Digital', desc: 'Lanzamiento de plataforma digital para rastreo y cotizaciones online' },
        ],
      },
      offices: {
        title: 'Nuestras',
        titleHighlight: 'Oficinas',
        locations: [
          { city: 'Vila Velha/ES', type: 'Matriz', address: 'Rua Sete de Junho, 33, Sala 506, Coqueiral de Itaparica – CEP 29.102-310' },
          { city: 'Belo Horizonte/MG', type: 'Sucursal', address: 'Rua Tomé de Souza, 830, Sala 1602, Savassi – CEP 30.140-136' },
          { city: 'Itajaí/SC', type: 'Sucursal', address: 'Rua Samuel Heusi, nº 463, Sala 606, Centro – CEP 88.301-320' },
          { city: 'São Paulo/SP', type: 'Sucursal', address: 'Avenida Paulista, 1439, Ed. Mario Wallace Simonsen Cochrane, 1º andar, cj 12 – CEP 01.311-200' },
        ],
      },
      partnerships: {
        title: 'Socios y',
        titleHighlight: 'Certificaciones',
        items: [
          { name: 'WCA World', desc: 'Mayor red global de agentes de carga, con presencia en 196 países', logo: 'https://customer-assets.emergentagent.com/job_global-shipping-22/artifacts/s8xmakb6_SELO%20WCA%20WORLD.jpg' },
          { name: 'IATA', desc: 'Asociación Internacional de Transporte Aéreo', logo: 'https://customer-assets.emergentagent.com/job_global-shipping-22/artifacts/mlok2810_SELO%20IATA%20.png' },
        ],
      },
      social: {
        title: 'Sigue a',
        titleHighlight: 'Well Cargo',
        subtitle: 'Mantente al día con noticias de comercio exterior, tips de logística y bastidores de nuestra operación.',
        linkedin: {
          title: 'LinkedIn',
          handle: '@wellcargobr',
          url: 'https://www.linkedin.com/company/well-cargo/',
          followers: '2.5K',
          description: 'Insights sobre comercio exterior, tendencias del mercado y novedades de la empresa',
          posts: [
            {
              type: 'article',
              title: '5 tendencias de logística internacional para 2025',
              engagement: '234 me gusta • 45 comentarios',
              time: 'Hace 2 días',
            },
            {
              type: 'update',
              title: '¡Nueva oficina en São Paulo! Expandiendo nuestra presencia para atenderlos mejor.',
              engagement: '189 me gusta • 32 comentarios',
              time: 'Hace 1 semana',
            },
            {
              type: 'tips',
              title: 'Cómo reducir costos en transporte marítimo: guía completa',
              engagement: '312 me gusta • 67 comentarios',
              time: 'Hace 2 semanas',
            },
          ],
        },
        instagram: {
          title: 'Instagram',
          handle: '@wellcargobr',
          url: 'https://www.instagram.com/wellcargobr/',
          followers: '1.8K',
          description: 'Bastidores, equipo, operaciones y cultura Well Cargo',
          posts: [
            {
              image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400&q=80',
              likes: '156',
              caption: '¡Otro embarque rumbo a Europa! 🚢',
            },
            {
              image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80',
              likes: '203',
              caption: 'Velocidad que hace la diferencia ✈️',
            },
            {
              image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&q=80',
              likes: '178',
              caption: '¡Equipo Well Cargo reunido! 💚',
            },
            {
              image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&q=80',
              likes: '142',
              caption: 'Cobertura nacional 🚛',
            },
          ],
        },
        cta: 'Síguenos',
      },
      team: {
        title: 'Nuestro',
        titleHighlight: 'Equipo',
        subtitle: 'Profesionales apasionados por la logística y comprometidos con su éxito.',
        stats: [
          { value: '40+', label: 'Colaboradores' },
          { value: '8+', label: 'Años exp. promedio' },
          { value: '100%', label: 'Dedicación' },
        ],
      },
      testimonials: {
        title: 'Lo que dicen',
        titleHighlight: 'nuestros clientes',
        items: [
          {
            quote: 'Well Cargo no es solo un proveedor de servicios, es un socio estratégico. El soporte consultivo hizo toda la diferencia en nuestra expansión internacional.',
            author: 'Carlos Mendes',
            role: 'CEO',
            company: 'TechBras Exportadora',
          },
          {
            quote: 'Profesionalismo, transparencia y resultados. Es lo que encontramos en Well Cargo por más de 5 años de asociación.',
            author: 'Ana Paula Silva',
            role: 'Gerente de Supply Chain',
            company: 'Industria Nacional',
          },
        ],
      },
      cta: {
        title: '¿Vamos a crecer juntos?',
        subtitle: 'Contáctanos y descubre cómo podemos impulsar tu operación de comercio exterior.',
        button: 'Hablar con el equipo',
      },
    },
  };

  const c = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a6b5c] via-[#0f4a3f] to-[#0a332b]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/10 text-[#3ECDA3] text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-6">
            {c.hero.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            {c.hero.title}<br />
            <span className="text-[#3ECDA3] italic">{c.hero.titleHighlight}</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
            {c.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-12 bg-white dark:bg-gray-900 -mt-10 relative z-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {c.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#3ECDA3] mb-1">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={statsVisible} />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
                {c.intro.title} <span className="text-[#3ECDA3] italic">{c.intro.titleHighlight}</span>
              </h2>
              <div className="space-y-4">
                {c.intro.paragraphs.map((p, i) => (
                  <p key={i} className="text-gray-600 dark:text-gray-300 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&q=80"
                alt="Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#3ECDA3] text-white p-6 rounded-2xl shadow-xl">
                <div className="text-4xl font-bold">15+</div>
                <div className="text-sm opacity-90">{language === 'pt' ? 'Anos de experiência' : language === 'en' ? 'Years of experience' : 'Años de experiencia'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.mission.title} <span className="text-[#3ECDA3] italic">{c.mission.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {c.mission.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center">
                  <div className="w-16 h-16 mx-auto bg-[#3ECDA3]/10 rounded-2xl flex items-center justify-center mb-6">
                    <IconComp className="w-8 h-8 text-[#3ECDA3]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.values.title} <span className="text-[#3ECDA3] italic">{c.values.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {c.values.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div
                  key={index}
                  className="group text-center p-6 rounded-2xl bg-gray-50 dark:bg-gray-800"
                >
                  <IconComp className="w-10 h-10 mx-auto mb-4 text-[#3ECDA3]" />
                  <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">{item.title}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a6b5c] to-[#0f4a3f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {c.social.title} <span className="text-[#3ECDA3] italic">{c.social.titleHighlight}</span>
            </h2>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto">{c.social.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* LinkedIn Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-[#0A66C2] p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
                    <Linkedin className="w-8 h-8 text-[#0A66C2]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">{c.social.linkedin.title}</h3>
                    <p className="text-white/70">{c.social.linkedin.handle}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-2xl">{c.social.linkedin.followers}</p>
                  <p className="text-white/70 text-sm">followers</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-400 mb-6">{c.social.linkedin.description}</p>
                <div className="space-y-4">
                  {c.social.linkedin.posts.map((post, index) => (
                    <div key={index} className="border-b border-gray-100 dark:border-gray-800 pb-4 last:border-0">
                      <p className="font-medium text-gray-900 dark:text-white mb-2">{post.title}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4" />
                          {post.engagement}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <a
                  href={c.social.linkedin.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full bg-[#0A66C2] hover:bg-[#004182] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  {c.social.cta}
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Instagram Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
                    <Instagram className="w-8 h-8 text-[#E4405F]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">{c.social.instagram.title}</h3>
                    <p className="text-white/70">{c.social.instagram.handle}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-2xl">{c.social.instagram.followers}</p>
                  <p className="text-white/70 text-sm">followers</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-400 mb-6">{c.social.instagram.description}</p>
                <div className="grid grid-cols-4 gap-2">
                  {c.social.instagram.posts.map((post, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={post.image}
                        alt={post.caption}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <div className="text-white text-center">
                          <Heart className="w-5 h-5 mx-auto fill-white" />
                          <span className="text-xs">{post.likes}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <a
                  href={c.social.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-opacity"
                >
                  {c.social.cta}
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.offices.title} <span className="text-[#3ECDA3] italic">{c.offices.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.offices.locations.map((office, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                <MapPin className="w-10 h-10 text-[#3ECDA3] mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white text-lg">{office.city}</h3>
                <span className="inline-block bg-[#3ECDA3]/10 text-[#3ECDA3] text-xs px-3 py-1 rounded-full mt-2 mb-3">{office.type}</span>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{office.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.partnerships.title} <span className="text-[#3ECDA3] italic">{c.partnerships.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {c.partnerships.items.map((partner, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-2xl p-8 text-center shadow-md hover:shadow-xl transition-shadow">
                {partner.logo ? (
                  <img 
                    src={partner.logo} 
                    alt={partner.name}
                    className="w-32 h-32 object-contain mx-auto mb-4"
                  />
                ) : (
                  <Award className="w-16 h-16 text-[#3ECDA3] mx-auto mb-4" />
                )}
                <h4 className="font-bold text-gray-900 dark:text-white text-xl">{partner.name}</h4>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-3">{partner.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.testimonials.title} <span className="text-[#3ECDA3] italic">{c.testimonials.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {c.testimonials.items.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8">
                <Quote className="w-10 h-10 text-[#3ECDA3] mb-4" />
                <p className="text-gray-700 dark:text-gray-300 text-lg italic mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#3ECDA3] rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">{testimonial.author}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1a6b5c] to-[#0f4a3f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{c.cta.title}</h2>
          <p className="text-gray-300 text-lg mb-8">{c.cta.subtitle}</p>
          <Link
            to="/contato"
            className="inline-flex items-center gap-2 bg-white text-[#1a6b5c] px-8 py-4 rounded-xl font-semibold hover:bg-[#3ECDA3] hover:text-white transition-all duration-300"
          >
            {c.cta.button}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
